/**
 * SpeakerNotes Component
 * Resizable text area for speaker notes below canvas
 */

import React, { useState, useEffect } from 'react';
import { Input } from 'antd';
import { AlignLeft } from 'lucide-react';
import { Scene } from '../../../../types/scene';
import { SceneService } from '../../services/sceneService';
import './SpeakerNotes.scss';

const { TextArea } = Input;

interface SpeakerNotesProps {
  sceneId: string;
  scene: Scene | null | undefined;
  projectId?: string;
  onNotesChange?: (sceneId: string, notes: string) => void;
  style?: React.CSSProperties;
}

export const SpeakerNotes: React.FC<SpeakerNotesProps> = ({ sceneId, scene, projectId, onNotesChange, style }) => {
  const [notes, setNotes] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // Load notes from scene
  useEffect(() => {
    if (scene?.notes) {
      setNotes(scene.notes);
    } else {
      setNotes('');
    }
  }, [scene]);

  // Update word count
  useEffect(() => {
    const words = notes.trim().split(/\s+/).filter(Boolean).length;
    setWordCount(words);
  }, [notes]);

  // Auto-save notes
  useEffect(() => {
    if (!sceneId || notes === (scene?.notes || '')) return;

    const timer = setTimeout(async () => {
      setIsSaving(true);
      try {
        console.log('💾 [SpeakerNotes] Saving notes for scene', sceneId, ':', notes);
        
        // If we have a callback, use it (it will update both DB and state)
        if (onNotesChange) {
          await onNotesChange(sceneId, notes);
        } else {
          // Fallback: just save to DB (for local projects)
          await SceneService.updateScene(sceneId, { notes }, projectId);
        }
        
        console.log('✅ [SpeakerNotes] Notes saved successfully');
      } catch (error) {
        console.error('❌ [SpeakerNotes] Failed to save notes:', error);
      } finally {
        setIsSaving(false);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [notes, sceneId, scene?.notes, projectId, onNotesChange]);

  return (
    <div className="speaker-notes" style={style}>
      <div className="notes-header">
        <div className="header-left">
          <AlignLeft className="notes-icon" size={16} />
          <span className="notes-title">Speaker Notes</span>
          {isSaving && <span className="saving-indicator">Saving...</span>}
        </div>
        <div className="header-right">
          <span className="word-count">{wordCount} words</span>
        </div>
      </div>

      <TextArea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Add your speaker notes here..."
        className="notes-textarea"
        autoSize={false}
      />
    </div>
  );
};
